import { useDeferredValue, useState } from 'react';
import { useNavigate } from 'react-router';
import { useHistorique } from '../features/historique/historique';
import { lireLErreur } from '../shared/api/client';
import { Bouton } from '../shared/ui/Bouton';
import { Carte } from '../shared/ui/Carte';
import { Saisie } from '../shared/ui/Champ';
import { EtatErreur } from '../shared/ui/EtatErreur';
import { EtatVide } from '../shared/ui/EtatVide';
import { Squelette } from '../shared/ui/Squelette';
import { Statut } from '../shared/ui/Statut';
import { Titre } from '../shared/ui/Titre';
import { dureeLisible, instantLisible } from '../shared/format/duree';
import { nomAffiche } from '../shared/format/designation';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'HISTORIQUE — `CDC-4` F-46, ET C'EST LE MÊME ÉCRAN DES DEUX CÔTÉS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Un écran à part, **distinct de l'écran d'arrivée**, et **le même des deux côtés** »
 * (cadrage §3.1, Q482). Il n'y a donc **aucun test de rôle dans cette page**, et il n'y a
 * nulle part où en poser un : c'est le serveur qui décide de ce qu'une personne voit — « un
 * tuteur ne voit que ses cours ; un élève voit tous les siens, tous tuteurs et toutes
 * structures confondus ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE LA LIGNE PORTE, ET RIEN DE PLUS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * La date et l'heure de début, **le titre**, les participants **par leur prénom seul**, la
 * **durée de présence de la personne qui regarde**, la **durée du cours**, et **l'état de
 * son tableau**. La description du cours n'y est pas — elle « figure aux champs interdits
 * de `CDC-4` §7 », et le contrat ne la rend donc pas : **il n'y a rien à masquer ici,
 * parce qu'il n'arrive rien**.
 *
 * **LES DEUX DURÉES SONT SUR LA MÊME LIGNE, ET C'EST VOULU** (Q485) : « la durée de
 * co-présence du cours sert **la structure qui facture**, la durée de présence sert **la
 * personne**. **Sur un cours groupé, elles diffèrent pour presque tout le monde.** » N'en
 * montrer qu'une obligerait à choisir laquelle des deux questions le produit répond.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUI N'EXISTE PAS SUR CET ÉCRAN, ET CHAQUE ABSENCE EST ÉCRITE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **Aucun tri.** « Ordre unique et non réglable » (DD-33).
 * - **Aucun filtre par date, par structure ni par état.** La recherche porte sur le prénom
 *   d'un participant et le titre, « et rien d'autre — **ce sont les deux seules valeurs que
 *   la ligne porte et que la personne connaît** ».
 * - **Aucun geste de retrait.** « Personne ne retire une ligne de son historique, ni tuteur
 *   ni élève. »
 * - **Aucune date sur un contenu qui n'est plus consultable.** « La durée de relecture est
 *   un réglage de structure **qu'une personne ne connaît pas**, et lui donner une date lui
 *   ferait lire ce réglage. »
 * - **Aucune cause et aucune date sur un participant anonymisé** : « une date dirait qu'un
 *   compte a été supprimé ce jour-là ».
 */
export function PageHistorique() {
  const [recherche, poserRecherche] = useState('');
  // **La frappe ne déclenche pas une requête par caractère.** `useDeferredValue` laisse
  // l'écran répondre pendant que la liste suit, sans compteur ni délai à régler — et sans
  // le `useEffect` qu'un anti-rebond maison demanderait.
  const rechercheDifferee = useDeferredValue(recherche.trim());
  const historique = useHistorique(
    rechercheDifferee === '' ? undefined : rechercheDifferee,
  );

  const tranches = historique.data?.pages ?? [];
  const lignes = tranches.flatMap((tranche) => tranche.lignes);
  // **Les totaux viennent de la PREMIÈRE tranche, et d'elle seule.** Ils ne dépendent pas
  // de ce qui est chargé à l'écran : ce sont les totaux de tous les cours, pas ceux des
  // lignes visibles. Les recalculer sur les lignes chargées les ferait grandir en
  // descendant la page, ce qui donnerait à croire qu'ils sont partiels.
  const totaux = tranches[0]?.totaux ?? [];

  return (
    <main className="mx-auto flex w-full max-w-espace flex-col gap-6 p-6">
      <Titre niveau={1}>Historique</Titre>

      {/* ── Les totaux, EN TÊTE D'ÉCRAN et nulle part ailleurs ── */}
      {totaux.length > 0 && (
        <Carte>
          <div className="flex flex-col gap-3">
            <Titre niveau={2} taille="18">
              Votre temps de cours
            </Titre>
            {/* « **Deux rattachements donnent deux totaux, jamais un seul.** Il additionne
                les durées de PRÉSENCE de la personne, partitionnées par son rattachement
                sur chaque cours — jamais par les structures présentes. » */}
            <ul className="flex flex-wrap gap-x-8 gap-y-3">
              {totaux.map((total) => (
                <li key={total.structure} className="flex flex-col">
                  <span className="text-13 text-texte-faible">
                    {total.structure}
                  </span>
                  <span className="text-20 font-weight-800 text-texte-fort">
                    {dureeLisible(total.secondes)}
                  </span>
                </li>
              ))}
            </ul>
            {/* La règle qui explique l'écart avec ce que la structure compte, dite une
                fois et sans chiffre : « la structure compte la durée entière du cours,
                sans partage ni prorata : **les deux comptes ne sont pas égaux, et c'est
                voulu** ». */}
            <p className="text-13 text-texte-faible">
              Ce total additionne le temps où vous étiez présent. Votre
              structure, elle, compte la durée entière de chaque cours&nbsp;:
              les deux comptes diffèrent.
            </p>
          </div>
        </Carte>
      )}

      <Saisie
        libelle="Rechercher"
        aide="Par le prénom d’un participant ou par le titre d’un cours."
        type="search"
        value={recherche}
        onChange={(evenement) => poserRecherche(evenement.target.value)}
      />

      {historique.isLoading && <Squelette lignes={6} />}

      {historique.isError && (
        <EtatErreur
          message={lireLErreur(historique.error).message}
          onReessayer={() => void historique.refetch()}
        />
      )}

      {historique.data !== undefined && lignes.length === 0 && (
        // **Deux vides sous une même apparence, et celui-ci est le premier des trois de
        // F-54** : « aucun cours passé ». La recherche en produit un troisième, qui n'est
        // pas un état vide du produit mais un résultat de recherche — le distinguer évite
        // de dire « vous n'avez aucun cours » à quelqu'un qui en a et cherche mal.
        <EtatVide
          titre={
            rechercheDifferee === ''
              ? 'Aucun cours passé'
              : 'Aucun cours ne correspond'
          }
        >
          {rechercheDifferee === ''
            ? 'Vos cours apparaîtront ici dès que le premier sera terminé.'
            : 'Essayez le prénom d’un participant, ou le titre du cours.'}
        </EtatVide>
      )}

      {lignes.length > 0 && (
        <ul className="flex flex-col gap-3">
          {lignes.map((ligne) => (
            <li key={ligne.coursId}>
              <LigneDHistorique ligne={ligne} />
            </li>
          ))}
        </ul>
      )}

      {historique.hasNextPage && (
        // « **La liste se charge par tranches, sans plafond de nombre de cours.** » Le
        // geste est explicite plutôt qu'automatique au défilement : sur un historique de
        // plusieurs années, un chargement au défilement rend la fin de page inatteignable.
        <Bouton
          variante="douce"
          enCours={historique.isFetchingNextPage}
          onClick={() => void historique.fetchNextPage()}
        >
          Charger les cours précédents
        </Bouton>
      )}
    </main>
  );
}

/** Ce qu'une tranche rend, ligne par ligne. Le type vient du contrat, jamais réécrit. */
type Ligne = NonNullable<
  ReturnType<typeof useHistorique>['data']
>['pages'][number]['lignes'][number];

/**
 * **Une ligne de l'historique.**
 *
 * Elle vit dans cette page et non dans `shared/ui/` parce qu'**elle connaît le domaine** :
 * l'état d'un tableau, la désignation d'un compte anonymisé, l'écart entre les deux durées.
 * `design-system-first` range les primitives sans connaissance du domaine dans la
 * bibliothèque, et **les composants métier près de leur écran**.
 */
function LigneDHistorique({ ligne }: { readonly ligne: Ligne }) {
  const navigate = useNavigate();
  const rouvrable = ligne.tableau === 'rouvrable';

  return (
    <Carte>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Titre niveau={2} taille="18">
              {ligne.titre}
            </Titre>
            <time dateTime={ligne.debut} className="text-13 text-texte-faible">
              {instantLisible(ligne.debut)}
            </time>
          </span>

          {/* **Les participants, PAR LEUR PRÉNOM SEUL.** Un compte anonymisé porte une
              désignation neutre, **la même pour tous**, et l'écran ne dit ni la cause ni
              la date. C'est le serveur qui pose `anonymise` ; le produit se contente de
              ne pas inventer un nom. */}
          <p className="text-14 text-texte">
            {ligne.participants
              .map((personne) => nomAffiche(personne.prenom, personne.anonymise))
              .join(', ')}
          </p>

          {/* **Les deux durées, nommées.** Sans étiquette, deux nombres côte à côte se
              lisent comme un intervalle. */}
          <p className="text-13 text-texte-faible">
            Votre présence&nbsp;: {dureeLisible(ligne.dureePresenceSecondes)}
            <span aria-hidden="true"> · </span>
            Durée du cours&nbsp;: {dureeLisible(ligne.dureeCoursSecondes)}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {rouvrable ? (
            // « **On ouvre la relecture depuis la ligne du cours dans l'historique**, et
            // c'est LE SEUL CHEMIN pour un participant. » Et « ouvrir la scène d'un cours
            // laisse une ligne de trace, quel que soit le lecteur » — la trace est posée
            // par le serveur à la lecture, pas par ce bouton.
            <Bouton
              taille="petite"
              variante="douce"
              onClick={() => void navigate(`/relecture/${ligne.coursId}`)}
            >
              Revoir le tableau
            </Bouton>
          ) : (
            // « L'écran dit que le contenu n'est plus consultable **et rien de plus**. »
            // Aucune date, aucun motif, et **aucun geste** : « l'historique n'en propose
            // plus le geste, au lieu de l'ouvrir sur un vide ».
            <Statut teinte="neutre" pleine={false}>
              Contenu plus consultable
            </Statut>
          )}
        </div>
      </div>
    </Carte>
  );
}
